import { formatCurrency } from '@shared/formatCurrency';

type TraditionalLTCQuote = {
  primaryAge: number;
  primaryGender: string;
  maritalClass: string;
  underwritingClass: string;
  benefitPool: number;
  dailyBenefitAmount: number;
  eliminationPeriod: number;
  bioPercent: string;
  premiumFrequency: string;
  primaryModalPremium: number;
  primaryTotalYearlyPremium: number;
  dp1Rider?: boolean;
  nfoRider?: boolean;
};

interface TraditionalLTCProductDetailsProps {
  quote: TraditionalLTCQuote;
}

function formatGender(gender: string): string {
  return gender === 'M' ? 'Male' : gender === 'F' ? 'Female' : gender;
}

function formatMaritalStatus(maritalClass: string): string {
  if (maritalClass === 'S') return 'Single';
  if (maritalClass === 'M') return 'Married';
  return maritalClass;
}

type ProductDetailEntry = {
  label: string;
  value: string | number;
  testId?: string;
};

type QuoteDetailSectionProps = {
  title: string;
  items: ProductDetailEntry[];
};

function QuoteDetailSection({ title, items }: QuoteDetailSectionProps) {
  return (
    <div className="select-none">
      <h4 className="text-primary-dark mb-2 text-sm font-bold">{title}</h4>
      <div className="space-y-1">
        {items.map((item, index) => (
          <p
            key={index}
            className="text-primary-dark/90 text-sm leading-relaxed"
            data-testid={item.testId}
          >
            {item.label && <span className="font-medium">{item.label}: </span>}
            {item.value}
          </p>
        ))}
      </div>
    </div>
  );
}

export function TraditionalLTCProductDetails({ quote }: TraditionalLTCProductDetailsProps) {
  const policyDetailItems: ProductDetailEntry[] = [
    { label: 'Age', value: quote.primaryAge, testId: 'quote-detail-age' },
    {
      label: 'Gender',
      value: formatGender(quote.primaryGender),
      testId: 'quote-detail-gender',
    },
    {
      label: 'Marital Status',
      value: formatMaritalStatus(quote.maritalClass),
      testId: 'quote-detail-marital-status',
    },
    {
      label: 'Underwriting Class',
      value: quote.underwritingClass,
      testId: 'quote-detail-underwriting-class',
    },
  ];

  const benefitItems: ProductDetailEntry[] = [
    {
      label: 'Benefit Pool',
      value: formatCurrency(Math.round(quote.benefitPool)),
      testId: 'quote-detail-benefit-pool',
    },
    {
      label: 'Daily Benefit',
      value: formatCurrency(Math.round(quote.dailyBenefitAmount)),
      testId: 'quote-detail-daily-benefit',
    },
    {
      label: 'Elimination Period',
      value: `${quote.eliminationPeriod} days`,
      testId: 'quote-detail-elimination-period',
    },
    {
      label: 'Benefit Inflation Option',
      value: quote.bioPercent === 'No BIO' ? 'None' : quote.bioPercent.replace(' BIO', ''),
    },
  ];

  const premiumItems: ProductDetailEntry[] = [
    {
      label: 'Payment Frequency',
      value: quote.premiumFrequency,
    },
    {
      label: 'Modal Premium',
      value: formatCurrency(Math.round(quote.primaryModalPremium)),
      testId: 'quote-detail-premium',
    },
    {
      label: 'Yearly Premium',
      value: formatCurrency(Math.round(quote.primaryTotalYearlyPremium)),
    },
    ...(quote.dp1Rider ? [{ label: '', value: '✓ DP1 Rider' }] : []),
    ...(quote.nfoRider ? [{ label: '', value: '✓ NFO Rider' }] : []),
  ];

  const sections = [
    { title: 'Policy Details', items: policyDetailItems },
    { title: 'Benefits', items: benefitItems },
    { title: 'Premiums', items: premiumItems },
  ];

  return (
    <div className="mt-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {sections.map((section, index) => (
          <QuoteDetailSection key={index} title={section.title} items={section.items} />
        ))}
      </div>
    </div>
  );
}

